import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  fetchDashboard, 
  fetchGraficasFavoritas, 
  fetchContadores, 
  fetchUltimosSensores, 
  fetchConsumoEnergia,
  toggleFavoriteGrafica,
  setDashboardError
} from '../actions/dashboard';

export const useDashboard = () => {
  const dispatch = useDispatch();
  const [refreshing, setRefreshing] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  
  const {
    isLoading,
    error,
    data,
    graficasFavoritas,
    contadores,
    ultimosSensores,
    consumoEnergia,
    userInfo,
    lastUpdated
  } = useSelector(state => state.dashboardReducer);

  // Marcar como carga inicial completada
  useEffect(() => {
    if (initialLoad && (!isLoading || error)) {
      const timer = setTimeout(() => {
        setInitialLoad(false);
      }, 1000); // Dar tiempo para mostrar el skeleton
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, error, initialLoad]);

  // Cargar datos completos del dashboard
  const loadDashboard = useCallback(async () => {
    try {
      setRefreshing(true);
      await dispatch(fetchDashboard());
    } catch (err) {
      dispatch(setDashboardError(err.message || 'Error al cargar el dashboard'));
    } finally {
      setRefreshing(false);
    }
  }, [dispatch]);

  // Cargar gráficas favoritas
  const loadGraficasFavoritas = useCallback(async () => {
    try {
      await dispatch(fetchGraficasFavoritas());
    } catch (err) {
      dispatch(setDashboardError(err.message || 'Error al cargar gráficas favoritas'));
    }
  }, [dispatch]);

  // Cargar contadores
  const loadContadores = useCallback(async () => {
    try {
      await dispatch(fetchContadores());
    } catch (err) {
      dispatch(setDashboardError(err.message || 'Error al cargar contadores'));
    }
  }, [dispatch]);

  // Cargar últimos sensores
  const loadUltimosSensores = useCallback(async () => {
    try {
      await dispatch(fetchUltimosSensores());
    } catch (err) {
      dispatch(setDashboardError(err.message || 'Error al cargar últimos sensores'));
    }
  }, [dispatch]);

  // Cargar consumo de energía
  const loadConsumoEnergia = useCallback(async () => {
    try {
      await dispatch(fetchConsumoEnergia());
    } catch (err) {
      dispatch(setDashboardError(err.message || 'Error al cargar consumo de energía'));
    }
  }, [dispatch]);

  // Marcar/desmarcar gráfica como favorita
  const handleToggleFavorite = useCallback(async (id, favorita) => {
    try {
      await dispatch(toggleFavoriteGrafica(id, favorita));
    } catch (err) {
      dispatch(setDashboardError(err.message || 'Error al cambiar estado de favorito'));
    }
  }, [dispatch]);

  // Refrescar todos los datos
  const refresh = useCallback(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Auto-refresh cada 5 minutos
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isLoading && !refreshing) {
        loadDashboard();
      }
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(interval);
  }, [isLoading, refreshing, loadDashboard]);

  return {
    // Estados
    loading: isLoading || refreshing,
    initialLoad,
    error,
    data,
    
    // Datos específicos
    graficasFavoritas,
    contadores,
    ultimosSensores,
    consumoEnergia,
    userInfo,
    lastUpdated,
    
    // Acciones
    refresh,
    loadGraficasFavoritas,
    loadContadores,
    loadUltimosSensores,
    loadConsumoEnergia,
    handleToggleFavorite,
    
    // Utilidades
    isStale: lastUpdated && (Date.now() - new Date(lastUpdated).getTime() > 5 * 60 * 1000)
  };
};
